/**
 * React Query hooks for bookings data
 * Provides data fetching, caching, and mutations for bookings
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  bookingsApi,
  type BookingSearchParams,
  type CreateBookingData,
  type UpdateBookingData,
  type RepairDetails,
  type DropoffData,
  type VehicleIntakeChecklist,
  type BookingTimelineEvent,
  type WorkflowAttachmentCategory,
  type WorkflowAttachmentWithMeta,
} from '../services/bookingsApi';
import { dashboardKeys } from './useDashboard';

/**
 * Query keys for bookings
 */
export const bookingKeys = {
  all: ['bookings'] as const,
  lists: () => [...bookingKeys.all, 'list'] as const,
  list: (params: BookingSearchParams) => [...bookingKeys.lists(), params] as const,
  today: () => [...bookingKeys.all, 'today'] as const,
  details: () => [...bookingKeys.all, 'detail'] as const,
  detail: (id: string) => [...bookingKeys.details(), id] as const,
  repairs: () => [...bookingKeys.all, 'repair'] as const,
  repair: (bookingId: string) => [...bookingKeys.repairs(), bookingId] as const,
  images: (bookingId: string) => [...bookingKeys.detail(bookingId), 'images'] as const,
  attachments: (bookingId: string, category?: string) => [...bookingKeys.detail(bookingId), 'attachments', category || 'all'] as const,
  timeline: (bookingId: string) => [...bookingKeys.detail(bookingId), 'timeline'] as const,
  checklist: (bookingId: string) => [...bookingKeys.detail(bookingId), 'checklist'] as const,
};

/**
 * Hook to fetch paginated list of bookings with filters
 */
export function useBookingList(params: BookingSearchParams = {}) {
  return useQuery({
    queryKey: bookingKeys.list(params),
    queryFn: () => bookingsApi.list(params),
    staleTime: 30_000,
  });
}

/**
 * Hook to fetch today's bookings
 */
export function useTodaysBookings() {
  return useQuery({
    queryKey: bookingKeys.today(),
    queryFn: bookingsApi.getToday,
    staleTime: 30_000,
    refetchInterval: 60_000, // Refresh every minute
  });
}

/**
 * Hook to fetch unverified widget bookings
 */
export function useUnverifiedBookings() {
  return useQuery({
    queryKey: [...bookingKeys.all, 'unverified'] as const,
    queryFn: bookingsApi.getUnverified,
    staleTime: 30_000,
    refetchInterval: 60_000, // Refresh every minute
  });
}

/**
 * Hook to fetch a single booking by ID
 */
export function useBooking(id: string | undefined) {
  return useQuery({
    queryKey: bookingKeys.detail(id!),
    queryFn: () => bookingsApi.getById(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

/**
 * Hook to fetch booking timeline events
 */
export function useBookingTimeline(bookingId: string | undefined) {
  return useQuery<BookingTimelineEvent[]>({
    queryKey: bookingKeys.timeline(bookingId!),
    queryFn: () => bookingsApi.getTimeline(bookingId!),
    enabled: !!bookingId,
    staleTime: 30_000,
  });
}

/**
 * Hook to fetch current status checklist
 */
export function useBookingChecklist(bookingId: string | undefined) {
  return useQuery({
    queryKey: bookingKeys.checklist(bookingId!),
    queryFn: () => bookingsApi.getChecklist(bookingId!),
    enabled: !!bookingId,
    staleTime: 10_000,
  });
}

/**
 * Hook to create a new booking
 */
export function useCreateBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateBookingData) => bookingsApi.create(data),
    onSuccess: () => {
      // Invalidate booking lists and dashboard
      queryClient.invalidateQueries({ queryKey: bookingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bookingKeys.today() });
      queryClient.invalidateQueries({ queryKey: dashboardKeys.metrics() });
    },
  });
}

/**
 * Hook to update a booking
 */
export function useUpdateBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBookingData }) =>
      bookingsApi.update(id, data),
    onSuccess: (booking) => {
      // Update the specific booking in cache
      queryClient.setQueryData(bookingKeys.detail(booking.id), booking);
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: bookingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bookingKeys.today() });
    },
  });
}

/**
 * Hook to confirm a booking
 */
export function useConfirmBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => bookingsApi.confirm(id),
    onSuccess: async (booking) => {
      // Force immediate refetch of booking detail to update UI
      await queryClient.refetchQueries({ queryKey: bookingKeys.detail(booking.id) });
      // Invalidate other related queries
      queryClient.invalidateQueries({ queryKey: bookingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bookingKeys.today() });
    },
  });
}

/**
 * Hook to resend confirmation email
 */
export function useResendConfirmation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => bookingsApi.resendConfirmation(id),
    onSuccess: (_, id) => {
      // Invalidate to refresh the booking details
      queryClient.invalidateQueries({ queryKey: bookingKeys.detail(id) });
    },
  });
}

/**
 * Hook to resend email notification for current booking status
 */
export function useResendBookingEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return bookingsApi.resendNotification(id);
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.detail(id) });
    },
  });
}

/**
 * Hook to cancel a booking
 */
export function useCancelBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason, notes }: { id: string; reason: string; notes?: string }) =>
      bookingsApi.cancel(id, reason, notes),
    onSuccess: (booking) => {
      // Invalidate all related queries to force refetch
      queryClient.invalidateQueries({ queryKey: bookingKeys.detail(booking.id) });
      queryClient.invalidateQueries({ queryKey: bookingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bookingKeys.today() });
      queryClient.invalidateQueries({ queryKey: dashboardKeys.metrics() });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['agenda'] });
    },
  });
}

/**
 * Hook to fetch repair details including status history
 */
export function useRepairDetails(bookingId: string | undefined) {
  return useQuery<RepairDetails>({
    queryKey: bookingKeys.repair(bookingId!),
    queryFn: () => bookingsApi.getRepairDetails(bookingId!),
    enabled: !!bookingId,
    staleTime: 30_000,
  });
}

/**
 * Hook to mark vehicle as dropped off (arrival)
 */
export function useVehicleDropoff() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: DropoffData }) =>
      bookingsApi.dropoff(id, data),
    onSuccess: async (_response, { id }) => {
      // Force immediate refetch of booking detail to update UI
      await queryClient.refetchQueries({ queryKey: bookingKeys.detail(id) });
      // Invalidate other related queries
      queryClient.invalidateQueries({ queryKey: bookingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bookingKeys.today() });
      queryClient.invalidateQueries({ queryKey: dashboardKeys.metrics() });
      queryClient.invalidateQueries({ queryKey: bookingKeys.repair(id) });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['agenda'] });
    },
  });
}

/**
 * Hook to fetch intake checklist
 */
export function useIntakeChecklist(bookingId: string | undefined) {
  return useQuery<VehicleIntakeChecklist>({
    queryKey: [...bookingKeys.detail(bookingId!), 'intake-checklist'] as const,
    queryFn: () => bookingsApi.getIntakeChecklist(bookingId!),
    enabled: !!bookingId,
    staleTime: 30_000,
  });
}

/**
 * Hook to update intake checklist
 */
export function useUpdateIntakeChecklist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      bookingId,
      data,
    }: {
      bookingId: string;
      data: Partial<Omit<VehicleIntakeChecklist, 'id' | 'bookingId' | 'createdAt' | 'updatedAt'>>;
    }) => bookingsApi.updateIntakeChecklist(bookingId, data),
    onSuccess: (checklist, { bookingId }) => {
      queryClient.setQueryData(
        [...bookingKeys.detail(bookingId), 'intake-checklist'],
        checklist
      );
    },
  });
}

/**
 * Hook to complete intake checklist
 */
export function useCompleteIntakeChecklist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (bookingId: string) => bookingsApi.completeIntakeChecklist(bookingId),
    onSuccess: (checklist, bookingId) => {
      queryClient.setQueryData(
        [...bookingKeys.detail(bookingId), 'intake-checklist'],
        checklist
      );
    },
  });
}

/**
 * Hook to fetch booking images
 */
export function useBookingImages(bookingId: string | undefined) {
  return useQuery({
    queryKey: bookingKeys.images(bookingId!),
    queryFn: () => bookingsApi.getImages(bookingId!),
    enabled: !!bookingId,
    staleTime: 10_000,
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });
}

/**
 * Hook to fetch workflow attachments for a booking
 */
export function useBookingAttachments(
  bookingId: string | undefined,
  category?: WorkflowAttachmentCategory
) {
  return useQuery<WorkflowAttachmentWithMeta[]>({
    queryKey: bookingKeys.attachments(bookingId!, category),
    queryFn: () => bookingsApi.getAttachments(bookingId!, category),
    enabled: !!bookingId,
    staleTime: 10_000,
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });
}

/**
 * Hook to delete a booking image
 */
export function useDeleteBookingImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ bookingId, imageId }: { bookingId: string; imageId: string }) =>
      bookingsApi.deleteImage(bookingId, imageId),
    onSuccess: (_data, { bookingId }) => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.images(bookingId) });
    },
  });
}

// Re-export types for convenience
export type {
  Booking,
  RepairDetails,
  VehicleIntakeChecklist,
  BookingImage,
  WorkflowAttachmentWithMeta,
  WorkflowAttachmentCategory,
  WorkflowChecklistRequirement,
} from '../services/bookingsApi';
